import { studyQueryKeys } from '@/config/queryKeys';
import { PROTEOFORM_DEFAULT_THRESHOLD_CPM, smIconSize, TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { useDateFormatter } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { searchProteoformSampleSummaries } from '@/services/proteoformSampleSummaries';
import {
  PaginatedProteoformSampleSummariesResponse,
  ProteoformSampleSummaryPublic,
  ProteoformSampleSummarySearchRequest,
  SPECIES_VALUES,
  StudyAssay,
  speciesLabel,
} from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useNavigate } from '@tanstack/react-router';
import { ColDef, GridOptions, ICellRendererParams } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { CircleArrowRight } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { createSetFilterParams, TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';

import SampleNameCell from './SampleNameCell';
import { useCanOpenSampleDetail } from './useCanOpenSampleDetail';

interface AssaySamplesTableProps {
  orgId: string;
  studyId: string;
  assaySummary: StudyAssay;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-assaySamples-pageSize`;
// ProteoformSampleSummarySortField — the counts are sortable here, which they were not on the
// sample DTO. `name` became `sample_name`; well_position/demux_index are not part of this grain.
const VALID_SORT_FIELDS = [
  'sample_name',
  'sample_group',
  'num_proteoform_groups',
  'num_ptms',
  'num_isoforms',
  'instrument_run_name',
  'assay_friendly_name',
  'species',
  'created_at',
  'created_by_full_name',
];
const DEFAULT_PAGE_SIZE = 25;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

export const AssaySamplesTable = function AssaySamplesTable({ assaySummary, orgId, studyId }: AssaySamplesTableProps) {
  const { t } = useTranslation(['studies', 'general']);
  const navigate = useNavigate();
  const formatDate = useDateFormatter();
  const gridRef = useRef<AgGridReact<ProteoformSampleSummaryPublic>>(null);
  // Branch on `assay_type` — StudyAssay carries it directly now, so no inference from the
  // free-text friendly name is needed. Broadscale has no roadmap date (2026-08-31 PR review);
  // its branch is removed here rather than left dead — see the tracking issue for the rest of
  // the app's Broadscale surface (route, run-creation form, generated types, e2e specs).
  const isTau = assaySummary.assay_type === 'tau_proteoform';
  const hasDetailRoute = isTau;
  const assayLabel = assaySummary.assay_friendly_name ?? t('columnHeaders.assay');
  const tableId = `assay-samples-table-${assaySummary.assay_id}`;

  // Study-wide totals ride along on the search envelope rather than needing their own request.
  // Stored as a scalar (not the object) so repeated pages don't churn identity and re-render.
  // Per the API contract these totals are fixed under both filters and threshold_cpm.
  const [numProteoformGroups, setNumProteoformGroups] = useState<number | null>(null);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<
        ProteoformSampleSummaryPublic,
        PaginatedProteoformSampleSummariesResponse,
        ProteoformSampleSummarySearchRequest
      >(
        studyQueryKeys.endpoints.proteoformSampleSummariesSearch(orgId, studyId),
        ({ limit, skip, sortBy, sortOrder, filters }) => {
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'sample_name';

          // The assay is scoped by the required top-level `assay_id`, not by a filter — this
          // endpoint returns one results table per assay by construction, so the old
          // assay_friendly_name equality filter is no longer needed.
          return {
            assay_id: assaySummary.assay_id,
            skip,
            limit,
            sort_by: validSortBy as ProteoformSampleSummarySearchRequest['sort_by'],
            sort_order: (sortOrder ?? 'asc') as 'asc' | 'desc',
            threshold_cpm: PROTEOFORM_DEFAULT_THRESHOLD_CPM,
            filters: filters as ProteoformSampleSummarySearchRequest['filters'],
          };
        },
        {
          fetcher: async request => {
            const response = await searchProteoformSampleSummaries(orgId, studyId, request);
            setNumProteoformGroups(response.num_proteoform_groups);
            return response;
          },
        },
      ),
    [orgId, studyId, assaySummary.assay_id],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<ProteoformSampleSummaryPublic>({
    studyId,
    tableId,
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const canOpenSample = useCanOpenSampleDetail();

  const colDefs: ColDef<ProteoformSampleSummaryPublic>[] = useMemo(
    () => [
      {
        cellRenderer: (params: ICellRendererParams<ProteoformSampleSummaryPublic>) => (
          <SampleNameCell canOpenSample={canOpenSample} data={params.data} />
        ),
        field: 'sample_name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'sample_group',
        headerName: t('columnHeaders.sampleGroup'),
        resizable: true,
        sortable: false,
        filter: false,
      },
      ...(isTau
        ? [
            {
              field: 'num_proteoform_groups',
              headerName: t('columnHeaders.proteoformCount'),
              resizable: true,
              sortable: true,
              filter: 'agNumberColumnFilter',
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<ProteoformSampleSummaryPublic>,
            {
              field: 'num_ptms',
              headerName: 'PTMs',
              resizable: true,
              sortable: true,
              filter: 'agNumberColumnFilter',
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<ProteoformSampleSummaryPublic>,
            {
              field: 'num_isoforms',
              headerName: 'Isoforms',
              resizable: true,
              sortable: true,
              filter: 'agNumberColumnFilter',
              valueFormatter: ({ value }: { value: number }) => value?.toLocaleString() ?? '0',
            } as unknown as ColDef<ProteoformSampleSummaryPublic>,
            // The is_control column is gone: `is_control` was removed from every DTO, and this
            // grid is now backed by proteoform sample summaries, whose rows carry no
            // sample_type either — controls are not a dimension of a results table.
          ]
        : []),
      {
        field: 'instrument_run_name',
        headerName: t('columnHeaders.instrumentRun'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'species',
        headerName: t('columnHeaders.species'),
        valueFormatter: ({ value }) => (value ? speciesLabel(value) : ''),
        resizable: true,
        sortable: false,
        // Enum column (SpeciesFieldFilters): equals/not_equals only, so the text filter
        // defaultColDef would supply is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('species', [...SPECIES_VALUES], speciesLabel),
      },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        resizable: true,
        sortable: true,
        filter: false,
        valueFormatter: ({ value }) => (value ? formatDate(value) : ''),
      },
    ],
    [isTau, t, formatDate, canOpenSample],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  const handleDetailsClick = () => {
    if (isTau) {
      navigate({ to: '/studies/$studyId/tau-samples', params: { studyId } });
    }
  };

  return (
    <Stack fullWidth gap={4} orientation="vertical">
      <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
        <Stack alignItems="center" gap={8} orientation="horizontal">
          <Text as="h3">{assayLabel} Samples</Text>
          {hasDetailRoute ? (
            <Button data-testid="assay-samples-detail-link" size="sm" visual="unstyled" onPress={handleDetailsClick}>
              <CircleArrowRight size={smIconSize} />
              {t('study.assayDetailLink')}
            </Button>
          ) : null}
        </Stack>
        <Stack gap={4} orientation="horizontal">
          <Text weight="bold">Total:</Text>
          <Text>{assaySummary.num_samples}</Text>
          {/* Study-wide totals come from the search envelope now — StudyPublicGetOne dropped
              them so the study GET no longer scans the proteoform fact table. */}
          {isTau && (
            <>
              <Text weight="bold">{t('columnHeaders.proteoformCount')}:</Text>
              <Text>{numProteoformGroups ?? 0}</Text>
            </>
          )}
        </Stack>
      </Stack>
      <Table
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        enableAdvancedFilter={false}
        enableResponsiveResize={false}
        getRowId={(params: { data: ProteoformSampleSummaryPublic }) => params.data.sample_id}
        gridOptions={gridOptions}
        onFilterChanged={handleFilterChanged}
        onFirstDataRendered={handleFirstDataRendered}
        onGridPreDestroyed={e => flushColumnState(e.api, tableId)}
        onGridReady={handleGridReady}
        onModelUpdated={handleModelUpdated}
        onPaginationChanged={handlePaginationChanged}
        pagination
        paginationPageSize={pageSize}
        paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
        ref={gridRef}
        resizeDebounceMs={150}
        rowModelType="serverSide"
        tableId={tableId}
      />
    </Stack>
  );
};
